/**
 * DashboardTabs — the tab bar of a dashboard with ``tabs[]`` (DSA 1.5).
 *
 * One row of ``role="tab"`` buttons on a light track, identical to the kit
 * ``Tabs`` (nuevo look Omnitok v3, approved 2026-10-24): every option reads as
 * a white bordered button; the selected one is filled with ``--omni-selected``
 * (the module colour) AND carries a check, so the state never depends on
 * colour alone. A tab's own colour shows as a dot on the unselected tabs.
 * Border width does not change on selection: no neighbour jumps. When the
 * reader arrived at the current tab through a KPI action ("Ver en
 * <tab> →") the bar also offers "← Volver a <tab anterior>", so the
 * jump never strands them: the way back is one click, named.
 *
 * The bar is presentation only. Which tab is active belongs to the
 * host (the portal keeps it in ``?tab=``; the builder preview keeps it
 * in state) — ``DashboardBody`` owns the fallback when the host does
 * not control it.
 */
import type { ReactNode } from "react";
import { ArrowLeft, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import { resolveLocalized, type DisplayLabel } from "./i18n-resolve";
import { useLang, type Lang } from "./i18n-binding";
import type { DashboardTab } from "./types";

export interface DashboardTabsProps {
  tabs: ReadonlyArray<DashboardTab>;
  activeId: string;
  onChange: (tabId: string) => void;
  /** Tab to offer as "← Volver a …" (set after an in-app jump). */
  returnTo?: string | null;
  /** Content at the right end of the row (the portal's Glosario button,
   *  encabezado compacto H1c, 2026-10-26). */
  end?: ReactNode;
}

export function tabLabel(tab: DashboardTab, lang: Lang): string {
  return resolveLocalized(tab.label as DisplayLabel, lang) || tab.id;
}

export function DashboardTabs({
  tabs,
  activeId,
  onChange,
  returnTo,
  end,
}: DashboardTabsProps): JSX.Element | null {
  const { t } = useTranslation();
  const lang = useLang();
  if (tabs.length === 0) return null;
  const back = returnTo && returnTo !== activeId ? tabs.find((x) => x.id === returnTo) : undefined;
  return (
    <div
      data-dashboard-tabs=""
      className="flex flex-wrap items-center justify-between gap-2"
    >
      <div
        role="tablist"
        aria-label={t("dashboard.tabs.aria")}
        className="inline-flex flex-wrap gap-1.5 rounded-[13px] border border-neutral-200 bg-neutral-100 p-1.5"
        onKeyDown={(event) => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
          const buttons = Array.from(
            event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
          );
          const index = buttons.indexOf(event.target as HTMLButtonElement);
          if (index < 0) return;
          event.preventDefault();
          const next = event.key === "Home"
            ? 0
            : event.key === "End"
              ? tabs.length - 1
              : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
          const nextTab = tabs[next];
          if (!nextTab) return;
          buttons[next]?.focus();
          onChange(nextTab.id);
        }}
      >
        {tabs.map((tab) => {
          const active = tab.id === activeId;
          const color = tab.color || undefined;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              data-tab-id={tab.id}
              onClick={() => onChange(tab.id)}
              className={`inline-flex min-h-[38px] items-center gap-[7px] rounded-[9px] border px-[15px] py-2 text-xs transition-[background-color,border-color,box-shadow] duration-150 ${
                active
                  ? "border-[var(--omni-selected)] bg-[var(--omni-selected)] font-bold text-white shadow-[0_2px_6px_color-mix(in_srgb,var(--omni-selected)_13%,transparent)]"
                  : "border-primary-line bg-white font-semibold text-neutral-600 shadow-card hover:border-primary/40 hover:bg-primary-soft"
              }`}
            >
              {active ? (
                <Check aria-hidden="true" size={14} strokeWidth={3} className="shrink-0" />
              ) : color ? (
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: color }}
                />
              ) : null}
              {tabLabel(tab, lang)}
            </button>
          );
        })}
      </div>
      {(back || end) && (
        <div className="flex items-center gap-2">
          {back && (
            <button
              type="button"
              data-tab-back=""
              onClick={() => onChange(back.id)}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
            >
              <ArrowLeft size={12} aria-hidden="true" />
              {t("dashboard.tabs.back", { tab: tabLabel(back, lang) })}
            </button>
          )}
          {end}
        </div>
      )}
    </div>
  );
}
